<template>
  <div class="main">
    <section v-if="template.headerLogoURIs" class="image-container">
      <Logos :logos="template.headerLogoURIs" />
    </section>
    <h2
      class="textFormat"
      :style="{ color: setTitleColor }"
      v-html="formatText(template.title)"
    ></h2>
    <CountdownTimer v-if="isJavascriptCountDown()" :time="extractTime()" />
    <section v-else-if="template.imageURI" class="image-container">
      <ImageURI :uri="template.imageURI" />
    </section>
    <h4
      class="textFormat"
      :style="{ color: setOverviewColor }"
      v-html="formatText(template.overview)"
    ></h4>
    <DisplayFields
      v-if="template.displayFields"
      :displayFields="template.displayFields"
      :theme="templateTheme"
    />
    <Inputs
      v-if="template.inputFields"
      @inputUpdated="inputsUpdated"
      :inputs="template.inputFields"
      :answers="inputData"
      :is-Disabled="isDisabled"
    />
    <p
      class="textFormat"
      :style="{ color: setConclusionColor }"
      v-html="formatText(template.conclusion)"
    ></p>
    <Buttons
      v-if="template.buttons && !isSilentAuth"
      @buttonClicked="buttonClicked"
      :buttons="template.buttons"
      :theme="templateTheme"
      :isDisabled="isDisabled"
    />
    <section v-if="template.footerLogoURIs" class="image-container">
      <FooterLogos :logos="template.footerLogoURIs" />
    </section>
  </div>
</template>

<script lang="ts">
import { InputAnswer } from "@/models/template";
import { defineComponent, PropType, ref } from "vue";
import ButtonsComponent from "./ButtonsComponent.vue";
import DisplayFields from "./DisplayFields.vue";
import Logos from "./Logos.vue";
import FooterLogos from "./FooterLogos.vue";
import Inputs from "./Inputs.vue";
import ImageURI from "./Image.vue";
import CountdownTimer from "./CountdownTimer.vue";
import {
  button,
  theme,
  uiContentJSON,
} from "@entersekt/types/build/3-5-0/endpoint";

const black = "#2c3e50";
const boldRegex = /(?:\*\*([^@]\w+?[^@](?:.+?))\*\*)/gs;
const italicRegex = /(?:\*([^@]\w+?[^@](?:.+?))\*)/gs;
const timerRegex = /\(((\d+))\)/g;
const externalLinkRegex = /\[(.*?)\]\((.*?)\)/g;

export default defineComponent({
  name: "DisplayTemplate",
  emits: ["buttonClicked"],
  components: {
    Buttons: ButtonsComponent,
    DisplayFields,
    Logos,
    Inputs,
    ImageURI,
    FooterLogos,
    CountdownTimer,
  },

  props: {
    template: {
      required: true,
      type: Object as PropType<uiContentJSON>,
    },
    templateTheme: {
      type: Object as PropType<theme>,
    },
    isSilentAuth: {
      type: Boolean,
    },
  },

  data() {
    return {
      isDisabled: false,
    };
  },

  provide() {
    return {
      formatTextFunction: this.formatText,
    };
  },

  setup() {
    const inputData = ref(new Map<string, number | string>());

    return { inputData };
  },

  watch: {
    template: {
      handler() {
        this.isDisabled = false;
      },
      deep: true,
    },
  },

  computed: {
    setTitleColor(): string {
      if (this.templateTheme?.titleTextColor) {
        return this.templateTheme?.titleTextColor;
      }
      return black;
    },

    setOverviewColor(): string {
      return this.templateTheme?.overviewTextColor
        ? this.templateTheme.overviewTextColor
        : black;
    },
    setConclusionColor(): string {
      return this.templateTheme?.conclusionTextColor
        ? this.templateTheme?.conclusionTextColor
        : black;
    },

    emphasisOverview(): string | undefined {
      if (this.template.summary) {
        return this.emphasisText(this.template.summary);
      }
      return this.emphasisText(this.template.overview);
    },
  },

  methods: {
    inputsUpdated(inputs: Map<string, string | number>) {
      this.inputData = inputs;
    },

    isJavascriptCountDown(): boolean {
      return this.template.imageURI?.includes("javascript:countdownTimer")
        ? true
        : false;
    },

    extractTime(): number | undefined {
      const matchRegex = this.template.imageURI?.match(timerRegex);
      if (matchRegex && matchRegex?.length > 0) {
        return +matchRegex[0].slice(1, matchRegex[0].length - 1);
      }
    },

    // uses regex to find and replace text wrapped with '**' to first make text bold,
    // then finds text wrapped with '*' to italicizes the text.
    emphasisText(text: string | undefined): string | undefined {
      if (!text) {
        return text;
      }
      const boldText = text.replace(boldRegex, "<strong>$1</strong>");
      return boldText.replace(italicRegex, "<em>$1</em>");
    },

    // Find matching string [text](URL) and converts it to an HTML clickable link
    replaceLink(text: string | undefined): string | undefined {
      if (!text) {
        return text;
      }
      return text.replace(
        externalLinkRegex,
        `<a href="$2" target="_blank">$1</a>`,
      );
    },

    formatText(text: string | undefined): string | undefined {
      //replace escaped newlines
      text = text?.replaceAll("\\n", "\n");
      let replacedText = this.replaceLink(text);
      return this.emphasisText(replacedText);
    },

    buttonClicked(action: button.action, actionNext?: button.actionNext) {
      const form = document.getElementById("inputs-form") as HTMLFormElement;
      if (form && !form.checkValidity() && action === "CONTINUE") {
        form.classList.add("was-validated");
      } else {
        this.isDisabled = true;
        const inputsArray: InputAnswer[] = [];
        this.inputData.forEach((value, key) => {
          inputsArray.push({ inputID: key, value });
        });
        this.$emit("buttonClicked", action, inputsArray, actionNext);
      }
    },
  },
});
</script>

<style>
.main {
  padding: 10px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
}

.image-container {
  padding-bottom: 30px;
}

.textFormat {
  white-space: pre-line;
}
</style>
